import React, { useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  FormGroup,
  Label,
  Input,
  Button,
  Alert,
  Table,
  Badge,
  Progress,
} from "reactstrap";

export default function PrPointCalculator() {
  const themeColor = "#7E1790";
  const themeGradient = "linear-gradient(135deg, #7E1790 0%, #5a1066 100%)";

  const [formData, setFormData] = useState({
    age: "",
    englishProficiency: "",
    australianWorkExp: "",
    overseasWorkExp: "",
    educationQualification: "",
    australianStudy: false,
    specialistEducation: false,
    accreditedLanguage: false,
    professionalYear: false,
    regionalStudy: false,
    partnerSkillsQualification: "",
    sponsorship: "",
    stateNomination: false,
  });

  const [totalPoints, setTotalPoints] = useState(0);
  const [breakdown, setBreakdown] = useState([]);
  const [visaEligibility, setVisaEligibility] = useState({
    subclass189: false,
    subclass190: false,
    subclass491: false,
  });

  const pointsTable = {
    age: {
      "18-24": 25,
      "25-32": 30,
      "33-39": 25,
      "40-44": 15,
      "45-49": 0,
    },
    englishProficiency: {
      competent: 0,
      proficient: 10,
      superior: 20,
    },
    australianWorkExp: {
      none: 0,
      "1-2": 5,
      "3-4": 10,
      "5-7": 15,
      "8+": 20,
    },
    overseasWorkExp: {
      none: 0,
      "3-4": 5,
      "5-7": 10,
      "8+": 15,
    },
    educationQualification: {
      diploma: 10,
      bachelor: 15,
      doctorate: 20,
    },
    partnerSkillsQualification: {
      none: 0,
      "no-partner-single": 10,
      "partner-skilled": 10,
      "partner-competent": 5,
      "partner-no-skills": 0,
    },
    sponsorship: {
      none: 0,
      "state-190": 5,
      "state-491": 15,
      "family-491": 15,
    },
  };

  const calculatePoints = () => {
    let points = 0;
    let details = [];

    if (formData.age) {
      const agePoints = pointsTable.age[formData.age];
      points += agePoints;
      if (agePoints > 0)
        details.push({ category: "Age", points: agePoints, icon: "👤" });
    }

    if (formData.englishProficiency) {
      const englishPoints = pointsTable.englishProficiency[formData.englishProficiency];
      points += englishPoints;
      details.push({ category: "English Proficiency", points: englishPoints, icon: "🗣️" });
    }

    if (formData.australianWorkExp) {
      const ausWorkPoints = pointsTable.australianWorkExp[formData.australianWorkExp];
      points += ausWorkPoints;
      if (ausWorkPoints > 0)
        details.push({ category: "Australian Work Experience", points: ausWorkPoints, icon: "💼" });
    }

    if (formData.overseasWorkExp) {
      const overWorkPoints = pointsTable.overseasWorkExp[formData.overseasWorkExp];
      points += overWorkPoints;
      if (overWorkPoints > 0)
        details.push({ category: "Overseas Work Experience", points: overWorkPoints, icon: "🌏" });
    }

    if (formData.educationQualification) {
      const eduPoints = pointsTable.educationQualification[formData.educationQualification];
      points += eduPoints;
      details.push({ category: "Education Qualification", points: eduPoints, icon: "🎓" });
    }

    if (formData.australianStudy) {
      points += 5;
      details.push({ category: "Australian Study Requirement", points: 5, icon: "📚" });
    }

    if (formData.specialistEducation) {
      points += 10;
      details.push({ category: "Specialist Education (STEM)", points: 10, icon: "🔬" });
    }

    if (formData.accreditedLanguage) {
      points += 5;
      details.push({ category: "Accredited Community Language", points: 5, icon: "🌐" });
    }

    if (formData.professionalYear) {
      points += 5;
      details.push({ category: "Professional Year in Australia", points: 5, icon: "👔" });
    }

    if (formData.regionalStudy) {
      points += 5;
      details.push({ category: "Study in Regional Australia", points: 5, icon: "🏞️" });
    }

    if (formData.partnerSkillsQualification) {
      const partnerPoints = pointsTable.partnerSkillsQualification[formData.partnerSkillsQualification];
      points += partnerPoints;
      if (partnerPoints > 0)
        details.push({ category: "Partner Skills", points: partnerPoints, icon: "👥" });
    }

    if (formData.sponsorship) {
      const sponsorPoints = pointsTable.sponsorship[formData.sponsorship];
      points += sponsorPoints;
      if (sponsorPoints > 0)
        details.push({ category: "State/Territory Nomination", points: sponsorPoints, icon: "🏛️" });
    }

    setTotalPoints(points);
    setBreakdown(details);

    setVisaEligibility({
      subclass189: points >= 65 && formData.sponsorship === "none",
      subclass190: points >= 65 && formData.sponsorship === "state-190",
      subclass491: points >= 65 && (formData.sponsorship === "state-491" || formData.sponsorship === "family-491"),
    });
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleReset = () => {
    setFormData({
      age: "", englishProficiency: "", australianWorkExp: "", overseasWorkExp: "",
      educationQualification: "", australianStudy: false, specialistEducation: false,
      accreditedLanguage: false, professionalYear: false, regionalStudy: false,
      partnerSkillsQualification: "", sponsorship: "", stateNomination: false,
    });
    setTotalPoints(0);
    setBreakdown([]);
    setVisaEligibility({ subclass189: false, subclass190: false, subclass491: false });
  };

  const getStatusColor = () => {
    if (totalPoints >= 80) return "success";
    if (totalPoints >= 65) return "info";
    if (totalPoints >= 50) return "warning";
    return "danger";
  };

  const getStatusMessage = () => {
    if (totalPoints >= 85) return "Excellent score! Highly competitive for invitation.";
    if (totalPoints >= 75) return "Very good score! Strong chance of invitation.";
    if (totalPoints >= 65) return "Minimum requirement met! You can submit EOI.";
    if (totalPoints >= 50) return "Close to minimum. Consider improving your profile.";
    return "Below minimum requirement. Boost your points!";
  };

  const getProgressPercentage = () => Math.min(100, (totalPoints / 100) * 100);

  return (
    <div style={{ background: "#F4F7FE", minHeight: "100vh", fontFamily: "'Inter', sans-serif" }}>
      {/* Header Section */}
      <div style={{ background: themeGradient, padding: "60px 0", marginBottom: "-100px" }}>
        <Container>
          <div className="text-center text-white">
            <h1 className="display-4 fw-bold mb-2">🇦🇺 Australia PR Points Calculator 2025</h1>
            <p className="lead opacity-75">Calculate your points for Australian Permanent Residency</p>
            <div className="d-flex justify-content-center gap-2 mt-4 flex-wrap">
              <Badge pill color="light" className="px-3 py-2 text-dark shadow-sm">Subclass 189</Badge>
              <Badge pill color="light" className="px-3 py-2 text-dark shadow-sm">Subclass 190</Badge>
              <Badge pill color="light" className="px-3 py-2 text-dark shadow-sm">Subclass 491</Badge>
            </div>
          </div>
        </Container>
      </div>

      <Container className="pb-5">
        <Row>
          <Col lg={8} className="mb-4">
            <Card className="shadow-lg border-0 rounded-4 overflow-hidden">
              <CardHeader className="bg-white border-0 pt-4 px-4">
                <h4 className="mb-1 text-dark fw-bold">📋 Your Information</h4>
                <p className="text-muted mb-0 small">Complete all sections for accurate point calculation</p>
              </CardHeader>
              <CardBody className="p-4">
                {/* Form Sections with custom header color */}
                {[
                  { id: "age", label: "👤 AGE (Max 30 pts)", type: "select", options: pointsTable.age, subText: "Peak points are awarded for ages 25-32" },
                  { id: "englishProficiency", label: "🗣️ ENGLISH PROFICIENCY (Max 20 pts)", type: "select", options: pointsTable.englishProficiency, subText: "IELTS, PTE, TOEFL iBT equivalents accepted" },
                ].map((section) => (
                  <div className="mb-4" key={section.id}>
                    <h6 className="text-uppercase fw-bold mb-3" style={{ color: themeColor, fontSize: "0.8rem", letterSpacing: "1px" }}>
                      {section.label}
                    </h6>
                    <FormGroup>
                      <Input type="select" name={section.id} value={formData[section.id]} onChange={handleChange} className="form-select-lg border-2 shadow-sm">
                        <option value="">Select option</option>
                        {/* {Object.keys(section.options).map(opt => <option key={opt} value={opt}>{opt} points</option>)} */}
                        {section.id === "age" && (
                          <>
                            <option value="18-24">18-24 years (25 points)</option>
                            <option value="25-32">25-32 years (30 points)</option>
                            <option value="33-39">33-39 years (25 points)</option>
                            <option value="40-44">40-44 years (15 points)</option>
                            <option value="45-49">45-49 years (0 points)</option>
                          </>
                        )}
                        {section.id === "englishProficiency" && (
                          <>
                            <option value="competent">Competent English - 0 points</option>
                            <option value="proficient">Proficient English - 10 points</option>
                            <option value="superior">Superior English - 20 points</option>
                          </>
                        )}
                      </Input>
                      <small className="text-muted mt-1 d-block">{section.subText}</small>
                    </FormGroup>
                  </div>
                ))}

                {/* Work Experience */}
                <div className="mb-4">
                  <h6 className="text-uppercase fw-bold mb-3" style={{ color: themeColor, fontSize: "0.8rem", letterSpacing: "1px" }}>
                    💼 SKILLED EMPLOYMENT
                  </h6>
                  <Row>
                    <Col md={6}>
                      <FormGroup>
                        <Label className="small fw-bold">Australian Experience</Label>
                        <Input type="select" name="australianWorkExp" value={formData.australianWorkExp} onChange={handleChange} className="form-select border-2">
                          <option value="">Select</option>
                          <option value="none">None (0 pts)</option>
                          <option value="1-2">1-2 years (5 pts)</option>
                          <option value="3-4">3-4 years (10 pts)</option>
                          <option value="5-7">5-7 years (15 pts)</option>
                          <option value="8+">8+ years (20 pts)</option>
                        </Input>
                      </FormGroup>
                    </Col>
                    <Col md={6}>
                      <FormGroup>
                        <Label className="small fw-bold">Overseas Experience</Label>
                        <Input type="select" name="overseasWorkExp" value={formData.overseasWorkExp} onChange={handleChange} className="form-select border-2">
                          <option value="">Select</option>
                          <option value="none">None (0 pts)</option>
                          <option value="3-4">3-4 years (5 pts)</option>
                          <option value="5-7">5-7 years (10 pts)</option>
                          <option value="8+">8+ years (15 pts)</option>
                        </Input>
                      </FormGroup>
                    </Col>
                  </Row>
                </div>

                {/* Education */}
                <div className="mb-4">
                   <h6 className="text-uppercase fw-bold mb-3" style={{ color: themeColor, fontSize: "0.8rem", letterSpacing: "1px" }}>
                    🎓 EDUCATION
                  </h6>
                  <Input type="select" name="educationQualification" value={formData.educationQualification} onChange={handleChange} className="form-select-lg border-2">
                    <option value="">Select qualification</option>
                    <option value="diploma">Diploma/Trade (10 pts)</option>
                    <option value="bachelor">Bachelor Degree (15 pts)</option>
                    <option value="doctorate">Doctorate/Masters Research (20 pts)</option>
                  </Input>
                </div>

                {/* Checklist Factors */}
                <div className="mb-4">
                   <h6 className="text-uppercase fw-bold mb-3" style={{ color: themeColor, fontSize: "0.8rem", letterSpacing: "1px" }}>
                    ⭐ ADDITIONAL FACTORS
                  </h6>
                  <div className="rounded-3 p-3 border-2 border" style={{ backgroundColor: "#F9F9FB" }}>
                    {[
                      { id: "australianStudy", title: "Australian Study Requirement", points: "+5" },
                      { id: "specialistEducation", title: "Specialist Education (STEM)", points: "+10" },
                      { id: "accreditedLanguage", title: "Accredited Community Language", points: "+5" },
                      { id: "professionalYear", title: "Professional Year Program", points: "+5" },
                      { id: "regionalStudy", title: "Study in Regional Australia", points: "+5" },
                    ].map(item => (
                      <FormGroup check className="mb-2 d-flex align-items-center justify-content-between" key={item.id}>
                        <Label check className="mb-0">
                          <Input type="checkbox" name={item.id} checked={formData[item.id]} onChange={handleChange} className="me-2" />
                          <span className="fw-semibold">{item.title}</span>
                        </Label>
                        <Badge style={{ backgroundColor: themeColor }}>{item.points}</Badge>
                      </FormGroup>
                    ))}
                  </div>
                </div>

                <div className="d-grid gap-2 d-md-flex justify-content-md-end mt-4 pt-3 border-top">
                  <Button color="link" className="text-decoration-none text-muted px-4" onClick={handleReset}>Reset</Button>
                  <Button size="lg" onClick={calculatePoints} className="px-5 shadow" style={{ backgroundColor: themeColor, border: "none" }}>
                    Calculate My Points
                  </Button>
                </div>
              </CardBody>
            </Card>
          </Col>

          {/* Sticky Results Panel */}
          <Col lg={4}>
            <div style={{ position: "sticky", top: "20px" }}>
              <Card className="shadow-lg border-0 rounded-4 overflow-hidden">
                <div className="text-white text-center py-5" style={{ background: totalPoints >= 65 ? themeGradient : "linear-gradient(135deg, #444 0%, #222 100%)" }}>
                  <div className="display-2 fw-bold mb-0">{totalPoints}</div>
                  <div className="text-uppercase fw-bold opacity-75 small">Total Points</div>
                </div>

                <CardBody className="p-4">
                  {totalPoints > 0 ? (
                    <>
                      <div className="mb-4">
                        <div className="d-flex justify-content-between mb-2">
                          <span className="small fw-bold text-muted text-uppercase">Score Progress</span>
                          <span className="small fw-bold" style={{ color: themeColor }}>{getProgressPercentage().toFixed(0)}%</span>
                        </div>
                        <Progress value={getProgressPercentage()} color={getStatusColor()} style={{ height: "8px" }} className="rounded-pill shadow-sm" />
                        <p className="small text-muted mt-3 mb-0 text-center italic">{getStatusMessage()}</p>
                      </div>

                      <Alert color={totalPoints >= 65 ? "success" : "danger"} className="border-0 rounded-3 shadow-sm">
                        <div className="fw-bold small">{totalPoints >= 65 ? "✓ ELIGIBLE FOR EOI" : "✕ NOT YET ELIGIBLE"}</div>
                        <div className="small opacity-75">{totalPoints >= 65 ? "You meet the 65 point threshold." : `You need ${65 - totalPoints} more points.`}</div>
                      </Alert>

                      <div className="mt-4">
                        <h6 className="fw-bold small text-muted text-uppercase mb-3">Visa Subclass Status</h6>
                        {['subclass189', 'subclass190', 'subclass491'].map(visa => (
                          <div key={visa} className={`p-2 rounded-3 mb-2 border ${visaEligibility[visa] ? 'bg-light border-success' : 'opacity-50'}`} style={{ fontSize: "0.85rem" }}>
                            <div className="d-flex justify-content-between align-items-center">
                              <span className="fw-bold">{visa.replace('subclass', 'Subclass ')}</span>
                              {visaEligibility[visa] ? <Badge color="success">Eligible</Badge> : <Badge color="secondary">N/A</Badge>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-4 text-muted">
                      <p className="small">Enter your details and click calculate to see your result.</p>
                    </div>
                  )}
                </CardBody>
              </Card>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
}